import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Guard factory that creates a guard checking for a specific privilege
 * @param requiredPrivilege The privilege required to access the route
 * @returns CanActivateFn that checks if user has the required privilege
 */
export function privilegeGuard(requiredPrivilege: string): CanActivateFn {
  return (route: ActivatedRouteSnapshot) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Get user privileges
    const userRolePrivilegeMap = authService.userRolePrivileges;
     if (!userRolePrivilegeMap) {
          console.warn(`[PrivilegeGuard] No privileges loaded - denying access to route requiring: ${requiredPrivilege}`);
          router.navigate(['/settings']);
          return false;
     }

    let userPrivileges: string[] | undefined;
    const mapEntry = userRolePrivilegeMap.entries().next().value;
    if (mapEntry) {
      const [role, privileges] = mapEntry;
      userPrivileges = privileges;
    }

    // If no privileges loaded, deny access for security
    if (!userPrivileges || !Array.isArray(userPrivileges)) {
      console.warn(`[PrivilegeGuard] No privileges loaded - denying access to route requiring: ${requiredPrivilege}`);
      // Only redirect if not already on settings route to avoid infinite loop
      const currentPath = route.pathFromRoot.map(r => r.url.map(segment => segment.path).join('/')).join('/');
      if (!currentPath.includes('settings')) {
        router.navigate(['/settings']);
      }
      return false;
    }

    // Check if user has the required privilege
    const hasPrivilege = userPrivileges.includes(requiredPrivilege);

    if (hasPrivilege) {
      console.log(`[PrivilegeGuard] Access granted to route requiring: ${requiredPrivilege}`);
      return true;
    }

    // User doesn't have required privilege
    console.warn(`[PrivilegeGuard] Access denied to route requiring: ${requiredPrivilege}. User privileges:`, userPrivileges);
    // Only redirect if not already on settings route to avoid infinite loop
    const currentPath = route.pathFromRoot.map(r => r.url.map(segment => segment.path).join('/')).join('/');
    if (!currentPath.includes('settings')) {
      router.navigate(['/settings']);
    }
    return false;
  };
}

/**
 * Alternative guard that reads privilege requirement from route data
 * Usage in routes: { path: 'charities', component: CharitiesComponent, canActivate: [privilegeGuardFromData], data: { requiredPrivilege: 'organization-list' } }
 */
export const privilegeGuardFromData: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredPrivilege = route.data['requiredPrivilege'] as string;

  if (!requiredPrivilege) {
    return true; // Allow access if no privilege specified
  }

  // Get user privileges
   const userRolePrivilegeMap = authService.userRolePrivileges;
   if (!userRolePrivilegeMap) {
        console.warn(`[PrivilegeGuard] No privileges loaded - denying access to route requiring: ${requiredPrivilege}`);
        // Only redirect if not already on settings route to avoid infinite loop
        const currentPath = route.pathFromRoot.map(r => r.url.map(segment => segment.path).join('/')).join('/');
        if (!currentPath.includes('settings')) {
          router.navigate(['/settings']);
        }
        return false;
   }

  let userPrivileges: string[] | undefined;
  const mapEntry = userRolePrivilegeMap.entries().next().value;
  if (mapEntry) {
    const [role, privileges] = mapEntry;
    userPrivileges = privileges;
  }

  // If no privileges loaded, deny access for security
  if (!userPrivileges || !Array.isArray(userPrivileges)) {
    // Only redirect if not already on settings route to avoid infinite loop
    const currentPath = route.pathFromRoot.map(r => r.url.map(segment => segment.path).join('/')).join('/');
    if (!currentPath.includes('settings')) {
      router.navigate(['/settings']);
    }
    return false;
  }

  // Check if user has the required privilege
  const hasPrivilege = userPrivileges.includes(requiredPrivilege);

  if (hasPrivilege) {
    return true;
  }

  // User doesn't have required privilege
  console.error(`[PrivilegeGuard] Access denied to route requiring: ${requiredPrivilege}. User privileges:`, userPrivileges);
  // Only redirect if not already on settings route to avoid infinite loop
  const currentPath = route.pathFromRoot.map(r => r.url.map(segment => segment.path).join('/')).join('/');
  if (!currentPath.includes('settings')) {
    router.navigate(['/settings']);
  }
  return false;
};

/**
 * Guard that checks if the user has the PORTAL_ADMIN role.
 * Redirects to /settings if the user does not have the role.
 */
export const portalAdminGuard: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.hasRole('PORTAL_ADMIN')) {
    return true;
  }

  console.warn('[PortalAdminGuard] Access denied - user does not have PORTAL_ADMIN role');
  router.navigate(['/settings']);
  return false;
};

/**
 * Guard that checks if the user has portal access (PORTAL_ADMIN or PORTAL_USER).
 * Broader than `portalAdminGuard` — used for Fund Profile routes that any portal user
 * (not just an admin) should reach. Redirects to /settings if the user has neither role.
 */
export const portalAccessGuard: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.hasPortalAccess()) {
    return true;
  }

  console.warn('[PortalAccessGuard] Access denied - user does not have portal access');
  router.navigate(['/settings']);
  return false;
};
